/**
 * The durability layer (PRD 6A rules 1 and 2).
 *
 * Every write is persisted to IndexedDB BEFORE the request is sent and removed
 * only once the server acknowledges it. A crash, a closed laptop or dead wifi
 * therefore cannot lose an edit: the outbox replays on next load.
 *
 * Each write carries a clientOpId. The server records it, so a replayed edit
 * returns the original response rather than applying twice.
 */

export type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving'; pending: number }
  | { kind: 'saved'; at: number }
  | { kind: 'error'; message: string; pending: number }

type Listener = (s: SaveState) => void

const DB_NAME = 'vantage-outbox'
const STORE = 'ops'

interface Op {
  id: string
  url: string
  method: string
  body: any
  queuedAt: number
  tries: number
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB_NAME, 1)
    r.onupgradeneeded = () => { if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE, { keyPath: 'id' }) }
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode)
    const req = fn(t.objectStore(STORE))
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

const listeners = new Set<Listener>()
let state: SaveState = { kind: 'idle' }
const emit = (s: SaveState) => { state = s; listeners.forEach(l => l(s)) }

export const onSaveState = (l: Listener) => { listeners.add(l); l(state); return () => { listeners.delete(l) } }
export const getSaveState = () => state

export const uid = () =>
  (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`)

let draining = false

async function drain(): Promise<void> {
  if (draining) return
  draining = true
  try {
    for (;;) {
      const ops = (await tx<Op[]>('readonly', s => s.getAll() as IDBRequest<Op[]>)) ?? []
      if (!ops.length) { emit({ kind: 'saved', at: Date.now() }); return }
      emit({ kind: 'saving', pending: ops.length })
      const op = ops.sort((a, b) => a.queuedAt - b.queuedAt)[0]
      try {
        const res = await fetch(op.url, {
          method: op.method,
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ ...op.body, clientOpId: op.id }),
          credentials: 'same-origin',
        })
        if (!res.ok) {
          const msg = ((await res.json().catch(() => ({}))) as any).error ?? `HTTP ${res.status}`
          if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
            // A request the server will never accept. Drop it rather than block
            // the queue forever, but surface it — never silently.
            await tx('readwrite', s => s.delete(op.id))
            emit({ kind: 'error', message: `Rejected: ${msg}`, pending: ops.length - 1 })
            continue
          }
          throw new Error(msg)
        }
        await tx('readwrite', s => s.delete(op.id))
      } catch (e: any) {
        op.tries++
        await tx('readwrite', s => s.put(op))
        emit({ kind: 'error', message: e?.message ?? 'offline', pending: ops.length })
        return   // stop; retry() or the next queue() resumes
      }
    }
  } finally { draining = false }
}

/** Queue a durable write. Resolves when it is persisted locally, not when sent. */
export async function queue(url: string, method: string, body: any): Promise<string> {
  const op: Op = { id: uid(), url, method, body, queuedAt: Date.now(), tries: 0 }
  await tx('readwrite', s => s.put(op))
  emit({ kind: 'saving', pending: 1 })
  void drain()
  return op.id
}

/** A write whose response the caller needs immediately (uploads, creates). */
export async function direct<T = any>(url: string, method: string, body?: any): Promise<T> {
  emit({ kind: 'saving', pending: 1 })
  const res = await fetch(url, {
    method,
    headers: body instanceof FormData ? undefined : { 'content-type': 'application/json' },
    body: body instanceof FormData ? body : body ? JSON.stringify({ ...body, clientOpId: uid() }) : undefined,
    credentials: 'same-origin',
  })
  const data: any = await res.json().catch(() => ({}))
  if (!res.ok) { emit({ kind: 'error', message: data.error ?? `HTTP ${res.status}`, pending: 0 }); throw new Error(data.error ?? `HTTP ${res.status}`) }
  emit({ kind: 'saved', at: Date.now() })
  return data as T
}

export async function get<T = any>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' })
  if (res.status === 401) { location.hash = '#/login'; throw new Error('unauthorised') }
  const data: any = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
  return data as T
}

export const retry = () => drain()
export const pendingCount = () => tx<number>('readonly', s => s.count())

/** Replay anything left over from a previous session, and whenever we come back online. */
export function startOutbox(): void {
  void drain()
  addEventListener('online', () => void drain())
  setInterval(() => { if (state.kind === 'error') void drain() }, 15000)
  addEventListener('beforeunload', e => {
    if (state.kind === 'saving' || state.kind === 'error') {
      e.preventDefault(); e.returnValue = ''
    }
  })
}
